import { ThemeProvider } from '@emotion/react';
import Box from '@mui/material/Box';
import { createTheme } from '@mui/material/styles';
import { db } from 'app/firebase';
import { useAppSelector } from 'app/hooks';
import Loader from 'components/atoms/loader';
import { selectAuth } from 'features/auth/authSlice';
import { selectChat, setCurrentIndex } from 'features/auth/chatSlice';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { IMessage } from 'interfaces/message.interface';
import { UserData } from 'interfaces/UserData.interface';
import MessageBox from 'components/molecules/MessageBox';
import * as React from 'react';
import { getMessageDataType, getRef } from 'utils/Firebase.util';
import { useDispatch } from 'react-redux';
import { Alert, AlertTitle } from '@mui/material';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

type Props = {
  data: any;
};

const DMessageBody = ({ data }: Props) => {
  const authState = useAppSelector(selectAuth);
  const chatState = useAppSelector(selectChat);
  const dispatch = useDispatch();
  const currentIndex = () =>
    chatState.inboxUsers.findIndex(
      user => user.user.id === userData.current?.id
    );
  const scrollIntoViewElem = React.useRef<HTMLDivElement>(null);
  const userData = React.useRef<UserData>(data);
  const user0 = require('assets/Profile/buku_default_avatar.png');
  const [messages, setMessages] = React.useState<IMessage[]>([]);
  const [isCreator, setIsCreator] = React.useState<boolean>(false);

  const [loading, setLoading] = React.useState<boolean>(false);
  // const [showTopBtn, setShowTopBtn] = React.useState(false);
  // const [scroll, setScroll] = React.useState<number>(0);

  // const showButton = () => {
  //   if (window.scrollY < scroll) {
  //     setShowTopBtn(true);
  //   } else {
  //     setShowTopBtn(false);
  //   }
  // };

  // console.log(window.scrollY, scroll);
  // React.useEffect(() => {
  //   window.addEventListener('scroll', showButton);
  //   return () => window.removeEventListener('scroll', showButton);
  // }, []);

  // React.useEffect(() => {
  //   setScroll(window.scrollY);
  // }, [messages]);

  React.useEffect(() => {
    if (data && authState) {
      userData.current = data.data as UserData;
      dispatch(setCurrentIndex(currentIndex()));
      const getDocs = async () => {
        setLoading(true);
        if (authState.id) {
          // setLoading(true);
          const docRef = await getRef(authState.id, userData.current.id);
          if (!docRef) {
            setMessages([]);
            return;
          } else {
            const chatDeleted = await getDoc(doc(db, 'chats', docRef));
            if (chatDeleted.exists()) {
              if (
                chatDeleted
                  .data()
                  .users.includes(`${authState.username}deleted`)
              ) {
                setMessages([]);
                return;
              }
            }
            const q = query(
              collection(db, 'chats', docRef, 'messages'),
              orderBy('date')
            );
            onSnapshot(q, doc => {
              doc.docChanges().forEach(message => {
                if (message.doc.data().invisible) {
                  if (message.doc.data().invisible.includes(authState.username))
                    return;
                }
                handleNewMessage(message.doc.data(), message.type);
              });
            });
            setLoading(false);
          }
        }
      };
      getDocs();
    }
    //eslint-disable-next-line
  }, [authState.id, data]);

  const handleNewMessage = async (newMessage: any, messageType: string) => {
    if (messageType === 'removed')
      setMessages(messages =>
        messages.filter(message => message.id !== newMessage.id)
      );
    else if (messageType === 'modified') {
      setMessages(prevMessages => {
        const changedMsgIndex = prevMessages.findIndex(
          msg => msg.id === newMessage.id
        );
        prevMessages.splice(changedMsgIndex, 1, newMessage);
        return prevMessages;
      });
    } else {
      setMessages(messages => [...messages, newMessage]);
      if (newMessage.uid !== authState.id && authState.id) {
        const docRef = await getRef(authState.id, newMessage.uid);
        if (docRef)
          await updateDoc(doc(db, 'chats', docRef, 'messages', newMessage.id), {
            isRead: true,
          });
      }
    }
  };

  React.useEffect(() => {
    if (scrollIntoViewElem.current) {
      scrollIntoViewElem.current.scrollIntoView({});
    }
  }, [scrollIntoViewElem, messages]);

  const getMessageSenderType = (item: number) => {
    if (item === authState.id) {
      return 'me';
    } else {
      return 'you';
    }
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={colortheme}>
        <Box
          sx={{
            paddingX: '10px',
            marginBottom: '130px',
            marginTop: '130px',
            position: 'relative',
          }}
        >
          {isCreator && (
            <Alert
              sx={{
                position: 'absolute',
                width: '90%',

                top: '120%',
                zIndex: 150,
              }}
              severity="success"
              onClose={() => setIsCreator(false)}
            >
              <AlertTitle>Success</AlertTitle>
              You have accepted {data.username}'s invite, —{' '}
              <strong>You're a creator now!</strong>
            </Alert>
          )}
          {messages.length > 0 && data ? (
            messages.map((item, i) => {
              const messageDataType = getMessageDataType(item.type);
              let repliedToType;
              if (item.repliedTo)
                repliedToType = getMessageDataType(item.repliedTo.type);
              return (
                <Box key={i}>
                  <MessageBox
                    isSent={item.isSent}
                    isRead={item.isRead}
                    reaction={item.reaction}
                    username={data.username}
                    messages={messages}
                    userId={userData.current.id}
                    msgId={item.id}
                    recieverAvatar={authState.avatar ? authState.avatar : user0}
                    senderAvatar={data?.avatar ? data.avatar : user0}
                    messageSender={getMessageSenderType(item.uid)}
                    post={item.post}
                    messageDataType={messageDataType}
                    nft={item.nft}
                    text={item.text}
                    repliedTo={item.repliedTo}
                    repliedToType={repliedToType}
                    senderId={item.uid}
                    isClicked={item.isClicked}
                    user={userData.current}
                    scrollIntoViewElem={scrollIntoViewElem}
                    setIsCreator={setIsCreator}
                  />
                </Box>
              );
            })
          ) : loading ? (
            <Loader />
          ) : (
            <></>
          )}
          {/* <BottomButton ref={scrollIntoViewElem} scale={showTopBtn ? true : false} /> */}
          <div ref={scrollIntoViewElem}></div>
        </Box>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default DMessageBody;
